/*
 * Copyright 2026 Sony Corporation
 */
import { APP_WINDOW, criteriaOf, type Verification, type VerificationSummary } from '@ips-jit/schema';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '../contexts/auth-context';

import { FoldButton } from './FoldedStrip';

/** The size the app's window is drawn at before it is scaled to fit, unless the check says how big it was. */
const SCREEN = APP_WINDOW.initial;
type ScreenSize = { width: number; height: number };
const WAITING_REFRESH_MS = 3_000;
const IDLE_REFRESH_MS = 20_000;

const describe = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback);

const STATE_COLOR = { sent: 'default', passed: 'success', failed: 'error', error: 'warning' } as const;
const STATE_LABEL = { sent: 'waiting', passed: 'passed', failed: 'failed', error: 'could not run' } as const;
const OUTCOME = {
  passed: { mark: '✓', color: 'success.main', label: 'passed' },
  failed: { mark: '✗', color: 'error.main', label: 'failed' },
  not_run: { mark: '–', color: 'text.disabled', label: 'not checked' },
} as const;

/**
 * The app's page as the device last showed it. The markup comes from the device, so it is shown in an iframe that
 * may run nothing and reach nothing: no scripts, no forms, no same origin.
 */
export const Screen = ({
  html,
  title,
  scale,
  size = SCREEN,
}: {
  html: string;
  title: string;
  scale?: number;
  size?: ScreenSize;
}) => {
  const box = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(scale ?? 0.33);

  useEffect(() => {
    if (scale !== undefined || !box.current || typeof ResizeObserver === 'undefined') {
      return;
    }
    const element = box.current;
    const observer = new ResizeObserver(() => setFit(element.clientWidth / size.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, [scale, size.width]);

  return (
    <Box
      ref={box}
      sx={{
        width: scale === undefined ? '100%' : size.width * scale,
        height: size.height * fit,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <iframe
        title={title}
        srcDoc={html}
        sandbox=""
        referrerPolicy="no-referrer"
        style={{
          width: size.width,
          height: size.height,
          border: 0,
          transform: `scale(${fit})`,
          transformOrigin: '0 0',
          pointerEvents: 'none',
          background: 'white',
        }}
      />
    </Box>
  );
};

export interface DeviceScreenProps {
  projectId: string;
  /** Whether a version of the app runs on a device, so there is a screen to look at. */
  running: boolean;
  /** Folds the column the panel is in. */
  onFold?: () => void;
}

/** The newest check of the app on the device: what was done, how it went, and the page as it looked. */
export const DeviceScreen = ({ projectId, running, onFold }: DeviceScreenProps) => {
  const { api } = useAuth();
  const [stepsOpen, setStepsOpen] = useState(false);
  const [latest, setLatest] = useState<VerificationSummary>();
  const [full, setFull] = useState<Verification>();
  const [label, setLabel] = useState<string>();
  const [error, setError] = useState<string>();
  const [large, setLarge] = useState(false);

  const load = useCallback(async () => {
    try {
      const [newest] = await api.listVerifications(projectId);
      // Ids sort by time. A check just asked for may not be in the list yet; it stays until the list has it.
      setLatest((shown) => (shown && (!newest || shown.verification_id > newest.verification_id) ? shown : newest));
    } catch (caught) {
      setError(describe(caught, 'Could not load the checks.'));
    }
  }, [api, projectId]);

  const waiting = latest?.state === 'sent';
  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), waiting ? WAITING_REFRESH_MS : IDLE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [load, waiting]);

  // The markup is only fetched for a finished check, and once.
  const latestId = latest && latest.state !== 'sent' ? latest.verification_id : undefined;
  useEffect(() => {
    if (!latestId) {
      return;
    }
    let cancelled = false;
    api.getVerification(projectId, latestId).then(
      (v) => {
        if (!cancelled) {
          setFull(v);
          setLabel(v.snapshots.at(-1)?.label);
        }
      },
      (caught: unknown) => !cancelled && setError(describe(caught, 'Could not load the screen.'))
    );
    return () => {
      cancelled = true;
    };
  }, [api, projectId, latestId]);

  const shown = full && full.verification_id === latest?.verification_id ? full : undefined;
  const withMarkup = shown?.snapshots.filter((s) => s.html) ?? [];
  const snapshot = withMarkup.find((s) => s.label === label) ?? withMarkup.at(-1);
  const title = latest ? `Screen of version ${latest.version}` : 'Screen';
  const criteria = latest ? criteriaOf(latest.steps, latest.results) : [];
  // The window the device's page had, so the picture is drawn at the size it was taken at.
  const size: ScreenSize = shown?.page?.layout?.viewport ?? SCREEN;
  // A failed step is why a check failed, so the steps open by themselves then.
  const failedStep = latest?.results.some((r) => !r.ok && !r.skipped) ?? false;

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label="Runtime screen"
      sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1">Runtime screen</Typography>
        {onFold && <FoldButton label="Runtime screen" side="right" onFold={onFold} />}
      </Stack>
      <Stack spacing={1} sx={{ mt: 1.5, minHeight: 0, overflowY: 'auto', mr: -1, pr: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {!latest ? (
          <Typography variant="body2" color="text.secondary">
            {running
              ? 'No check yet. The assistant checks each version it delivers on the runtime and shows its screen here.'
              : 'Nothing runs on a device yet.'}
          </Typography>
        ) : (
          <>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
              <Chip size="small" color={STATE_COLOR[latest.state]} label={STATE_LABEL[latest.state]} />
              <Typography variant="caption" color="text.secondary">
                {`Version ${latest.version} · ${latest.source === 'assistant' ? 'checked by the assistant' : 'asked by you'} · ${new Date(latest.created_at).toLocaleTimeString()}`}
              </Typography>
            </Stack>
            {waiting && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Waiting for the device...
                </Typography>
                <LinearProgress sx={{ mt: 0.5 }} />
              </Box>
            )}
            {latest.error && (
              <Typography variant="body2" color="warning.light">
                {latest.error}
              </Typography>
            )}
            {snapshot?.html && (
              <>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="Open the screen larger"
                  onClick={() => setLarge(true)}
                  onKeyDown={(event) => event.key === 'Enter' && setLarge(true)}
                  sx={{ cursor: 'zoom-in' }}
                >
                  <Screen html={snapshot.html} title={title} size={size} />
                </Box>
                {withMarkup.length > 1 && (
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {withMarkup.map((s) => (
                      <Chip
                        key={s.label}
                        size="small"
                        label={s.label}
                        variant={s.label === snapshot.label ? 'filled' : 'outlined'}
                        onClick={() => setLabel(s.label)}
                      />
                    ))}
                  </Stack>
                )}
              </>
            )}
            {criteria.length > 0 && (
              <Stack component="ul" spacing={0.5} sx={{ listStyle: 'none', p: 0, m: 0 }} aria-label="Criteria">
                {criteria.map((c) => (
                  <Typography component="li" key={c.criterion} variant="body2" sx={{ display: 'flex', gap: 0.75 }}>
                    <Box
                      component="span"
                      aria-label={OUTCOME[c.outcome].label}
                      sx={{ color: OUTCOME[c.outcome].color, fontWeight: 700 }}
                    >
                      {OUTCOME[c.outcome].mark}
                    </Box>
                    <Box component="span">
                      {c.criterion}
                      {c.detail && <Box component="span" sx={{ color: 'text.secondary' }}>{` — ${c.detail}`}</Box>}
                    </Box>
                  </Typography>
                ))}
              </Stack>
            )}
            {latest.results.length > 0 && !(stepsOpen || failedStep) && (
              <Button
                size="small"
                aria-expanded={false}
                onClick={() => setStepsOpen(true)}
                sx={{ alignSelf: 'flex-start' }}
              >
                {`▸ ${latest.results.length} check steps`}
              </Button>
            )}
            {latest.results.length > 0 && (stepsOpen || failedStep) && (
              <Stack component="ol" spacing={0.5} sx={{ listStyle: 'none', p: 0, m: 0 }} aria-label="Steps">
                {latest.results.map((r) => (
                  <Typography component="li" key={r.index} variant="caption" sx={{ display: 'flex', gap: 0.75 }}>
                    <Box
                      component="span"
                      sx={{
                        color: r.ok ? 'success.main' : r.skipped ? 'text.disabled' : 'error.main',
                        fontWeight: 700,
                      }}
                    >
                      {r.ok ? '✓' : r.skipped ? '–' : '✗'}
                    </Box>
                    <Box component="span" sx={{ color: r.ok ? 'text.secondary' : 'text.primary' }}>
                      {r.detail ?? `${r.action} ${r.target ?? ''}`}
                    </Box>
                  </Typography>
                ))}
              </Stack>
            )}
          </>
        )}
      </Stack>
      {snapshot?.html && (
        <Dialog open={large} onClose={() => setLarge(false)} maxWidth={false}>
          <DialogTitle>{`${title} · ${snapshot.label}`}</DialogTitle>
          <DialogContent>
            <Screen
              html={snapshot.html}
              title={`${title}, large`}
              size={size}
              scale={Math.min(1, (typeof window === 'undefined' ? size.width : window.innerWidth * 0.85) / size.width)}
            />
          </DialogContent>
        </Dialog>
      )}
    </Paper>
  );
};
